import * as duckdb from '@duckdb/duckdb-wasm'
import {
  Field,
  Float64,
  List,
  tableFromArrays,
  vectorFromArray,
  Utf8,
} from 'apache-arrow'

import { DuckDBSchemaType } from './utils/types'

import { CONNECTION_TIMEOUT, MANUAL_BUNDLES } from './utils/const'

import { handleCatchedError } from '@/helpers/common'

let db: duckdb.AsyncDuckDB | undefined
let connection: duckdb.AsyncDuckDBConnection | undefined
let initPromise: Promise<void> | undefined

const withTimeout = <T>(p: Promise<T>, ms = 20000) =>
  Promise.race([
    p,
    new Promise<T>((_, rej) => setTimeout(() => rej(new Error(`DuckDB timeout ${ms}ms`)), ms)),
  ])

export function useDuckDB() {
  async function init(): Promise<void> {
    if (db) return
    if (initPromise) return initPromise

    const initialize = async (): Promise<void> => {
      try {
        // select a bundle based on browser checks
        const bundle = await duckdb.selectBundle(MANUAL_BUNDLES)
        // instantiate the asynchronous version of DuckDB-wasm
        const worker = new Worker(bundle.mainWorker!)
        const logger = new duckdb.ConsoleLogger()
        db = new duckdb.AsyncDuckDB(logger, worker)
        await withTimeout(db.instantiate(bundle.mainModule, bundle.pthreadWorker), CONNECTION_TIMEOUT)
        connection = await withTimeout(db.connect(), CONNECTION_TIMEOUT)
      } catch (error) {
        initPromise = undefined
        handleCatchedError(error as string, {
          context: 'DuckDB initialization',
        })
        throw error
      }
    }

    initPromise = initialize()
    return initPromise
  }

  async function getConnection() {
    // wait for initialization if it's in progress
    if (initPromise && !connection) {
      try {
        await Promise.race([
          initPromise,
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error('Initialization timeout')),
              CONNECTION_TIMEOUT,
            ),
          ),
        ])
      } catch (error) {
        handleCatchedError(error as string, {
          context: 'Waiting for DuckDB initialization',
        })
        return
      }
    }

    // fallback polling for cases where init() wasn't called
    if (!connection) {
      const start = Date.now()
      return new Promise<duckdb.AsyncDuckDBConnection | null>(resolve => {
        const interval = setInterval(() => {
          if (connection) {
            clearInterval(interval)
            resolve(connection)
          } else if (Date.now() - start > CONNECTION_TIMEOUT) {
            clearInterval(interval)
            resolve(null)
          }
        }, 100)
      })
    }

    return connection
  }

  async function disconnect() {
    connection?.close()
    connection = undefined
    db = undefined
    initPromise = undefined
  }

  async function createTable(
    tableName: string,
    schema: Record<string, DuckDBSchemaType>,
  ) {
    const connection = await getConnection()
    if (!connection) return
    try {
      await connection.query(`DROP TABLE IF EXISTS ${tableName}`)
      await connection.query(
        `CREATE TABLE ${tableName} (${Object.entries(schema)
          .map(([key, value]) => `${key} ${value}`)
          .join(', ')})`,
      )
    } catch (error) {
      handleCatchedError(error as string, {
        tableName,
        schema,
        context: 'Creating table',
      })
    }
  }

  async function dropTable(tableName: string) {
    const connection = await getConnection()
    if (!connection) return
    try {
      await connection.query(`DROP TABLE IF EXISTS ${tableName}`)
    } catch (error) {
      handleCatchedError(error as string, {
        tableName,
        context: 'Dropping table',
      })
    }
  }

  async function query<T extends Record<string, any>>(
    query: string,
    params?: unknown[],
  ) {
    try {
      const connection = await getConnection()
      if (!connection) return
      const queryWithParams = params?.length
        ? params.reduce(
          (acc: string, param: unknown) => acc.replace('?', `'${param}'`),
          query,
        )
        : query
      const result = await connection.query(queryWithParams)
      const rows = result.toArray() as Array<{ toJSON: () => T }>
      return rows.map(row => row.toJSON())
    } catch (error) {
      handleCatchedError(error as string, { query, params })
      return
    }
  }

  async function pushData<T extends Record<string, any>>(
    tableName: string,
    data: T[],
    schema: Record<string, DuckDBSchemaType>,
  ) {
    const connection = await getConnection()
    if (!connection) return

    const cols = Object.keys(schema)
    const arrays: Record<string, any> = {}

    for (const col of cols) {
      switch (schema[col]) {
        case 'DATE':
        case 'VARCHAR': {
          const listData = data.map(item => item[col] ?? null)
          arrays[col] = vectorFromArray(listData, new Utf8())
          break
        }
        case 'DOUBLE': {
          const listData = data.map(item => item[col] ?? null)
          arrays[col] = vectorFromArray(listData, new Float64())
          break
        }
        case 'VARCHAR[]': {
          const listType = new List(new Field('item', new Utf8(), true))
          const listData = data.map(item => {
            const value = item[col]
            if (!value) return []
            if (Array.isArray(value))
              return value.map(item => (item ? String(item) : null))
            return [String(value)]
          })
          arrays[col] = vectorFromArray(listData, listType)
          break
        }
        default:
          throw new Error(`Unknown schema: ${schema[col]}`)
      }
    }
    try {
      const table = tableFromArrays(arrays)
      await connection.insertArrowTable(table as any, {
        name: tableName,
        create: false,
      })
    } catch (error) {
      handleCatchedError(error as string, { tableName, schema })
    }
  }

  return {
    init,
    disconnect,

    createTable,
    dropTable,

    pushData,

    query,
  }
}
